import type { ReactNode } from 'react';
import { Screen } from '@/components/layout/AppShell';
import { TopBar } from '@/components/layout/TopBar';

const CHARITIES = [
  {
    name: 'Technically Thespians',
    cause: 'Pacific Northwest theatre',
    description:
      'Support a nonprofit troupe of tech professionals producing live theatre in the Pacific Northwest.',
    href: 'https://www.technicallythespians.com/support-us',
    logo: '/charities/technically-thespians.png',
    logoBackground: 'bg-brand-600',
  },
  {
    name: 'Eastside Friends of Seniors',
    cause: 'Neighbors helping neighbors',
    description:
      'Support older adults with the practical help, meaningful connections, and community they deserve.',
    href: 'https://www.eastsidefriendsofseniors.org/donate',
    logo: '/charities/eastside-friends-of-seniors.png',
    logoBackground: 'bg-white',
  },
  {
    name: 'Dogs for Better Lives',
    cause: 'Assistance dogs',
    description:
      'Fund the training and placement of assistance dogs who make everyday life safer and more independent.',
    href: 'https://support.dogsforbetterlives.org/site/Donation2?df_id=2300&mfc_pref=T&2300.donation=form1',
    logo: '/charities/dogs-for-better-lives.png',
    logoBackground: 'bg-white',
  },
] as const;

export function DonateScreen() {
  return (
    <>
      <TopBar title="Donate" subtitle="Put a little good into the world" />
      <Screen>
        <section className="relative overflow-hidden rounded-2xl bg-ink-900 px-5 py-7 text-white shadow-sm dark:border dark:border-ink-800">
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-20 [background-image:radial-gradient(circle_at_1px_1px,currentColor_1px,transparent_0)] [background-size:18px_18px]"
          />
          <div className="relative max-w-md">
            <div className="mb-4 flex size-11 items-center justify-center rounded-full bg-brand-500 text-white">
              <HeartIcon />
            </div>
            <p className="font-script text-2xl leading-tight">
              The best applause is the kind that keeps going.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-ink-300">
              These organizations strengthen the communities around us. Choose a cause below to
              donate directly on its official website.
            </p>
          </div>
        </section>

        <ul className="flex flex-col gap-3" aria-label="Charities">
          {CHARITIES.map((charity) => (
            <li key={charity.name}>
              <a
                href={charity.href}
                target="_blank"
                rel="noreferrer"
                className="group grid grid-cols-[4.5rem_1fr_auto] items-center gap-3 rounded-2xl border border-ink-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md dark:border-ink-800 dark:bg-ink-900 dark:hover:border-brand-700"
                aria-label={`Donate to ${charity.name} (opens in a new tab)`}
              >
                <span
                  className={`flex h-14 w-18 items-center justify-center rounded-xl p-2 ${charity.logoBackground}`}
                >
                  <img
                    src={charity.logo}
                    alt={`${charity.name} logo`}
                    className="max-h-full max-w-full object-contain"
                  />
                </span>
                <span className="min-w-0">
                  <span className="text-ink-500 dark:text-ink-400 block text-xs font-semibold uppercase tracking-wide">
                    {charity.cause}
                  </span>
                  <span className="mt-0.5 block font-semibold leading-tight">{charity.name}</span>
                  <span className="text-ink-500 dark:text-ink-400 mt-1 block text-sm leading-snug">
                    {charity.description}
                  </span>
                </span>
                <span className="text-ink-400 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors" aria-hidden="true">
                  <ExternalLinkIcon />
                </span>
              </a>
            </li>
          ))}
        </ul>

        <p className="text-ink-500 dark:text-ink-400 px-4 text-center text-xs leading-relaxed">
          Donations are made directly to each organization. Line Runner does not process or store
          payment information.
        </p>
      </Screen>
    </>
  );
}

function Icon({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="size-6" aria-hidden="true">
      {children}
    </svg>
  );
}

function HeartIcon() {
  return (
    <Icon>
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.5 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </Icon>
  );
}

function ExternalLinkIcon() {
  return (
    <Icon>
      <path d="M14 5h5v5m0-5-8 8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M19 13v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </Icon>
  );
}